import { useState } from 'react'
import CopyLinkButton from '../../../components/Discord/CopyLinkButton'
import GuidelineTooltip from '../../../components/Discord/GuidelineTooltip'
import styles from '@/components/Discord/DiscordDocument.module.css'
import DiscordDocument from '@/components/Discord/DiscordDocument'

function DiscordPlatformManipulationPolicyPage() {
  const [language, setLanguage] = useState<'en' | 'cs'>('cs')
  const [inauthenticExamplesOpen, setInauthenticExamplesOpen] = useState(false)

  const getSectionUrl = (sectionId: string) => {
    return `${window.location.origin}/discord/safety/platform-manipulation-policy-explainer#${sectionId}`
  }

  return (
    <DiscordDocument
      language={language}
      onLanguageChange={setLanguage}
      heading={
        <div className={styles.titleSection}>
          <h1 className={styles.title}>
            {language === 'cs'
              ? 'Politika manipulace s platformou'
              : 'Platform Manipulation Policy'}
          </h1>
        </div>
      }
      notice={
        <>
          <div className={styles.disclaimer}>
            <div className={styles.disclaimerCard} lang="cs">
              <h3>Důležité upozornění</h3>
              <p>
                Nejsme přidruženi ani spojeni s Discord Inc. Toto je pouze komunitní překlad
                oficiální stránky o politice manipulace s platformou Discord dostupné na{' '}
                <a
                  href="https://discord.com/safety/platform-manipulation-policy-explainer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  discord.com/safety/platform-manipulation-policy-explainer
                </a>
                .
              </p>
              <p>
                Tento překlad nemusí být aktuální s oficiální politikou. Pokud si všimnete rozdílu
                mezi oficiální stránkou a touto stránkou, prosím, dejte nám vědět na našem Discord
                serveru.
              </p>
            </div>
            <div className={styles.disclaimerCard} lang="en">
              <h3>Important Notice</h3>
              <p>
                We are not affiliated with or associated with Discord Inc. This is only a community
                translation of the official Discord platform manipulation policy available at{' '}
                <a
                  href="https://discord.com/safety/platform-manipulation-policy-explainer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  discord.com/safety/platform-manipulation-policy-explainer
                </a>
                .
              </p>
              <p>
                This translation may not be up to date with the official policy. If you notice a
                difference between the official page and this page, please let us know on our
                Discord server.
              </p>
            </div>
          </div>
        </>
      }
    >
      {/* Disclaimer section - always show both languages */}

      {/* Quick Summary - Czech only */}
      {language === 'cs' && (
        <details className={styles.quickSummary} id="quick-summary">
          <summary>
            <h2>Rychlé shrnutí politiky</h2>
          </summary>
          <CopyLinkButton
            url={getSectionUrl('summary')}
            language={language}
            className={styles.summaryCopyLink}
          />

          <div className={styles.summaryGrid}>
            <div className={styles.summaryCard}>
              <h3>Zneužívání platformy</h3>
              <ul>
                <li>Žádný spam nebo nevyžádané hromadné zprávy</li>
                <li>Zákaz používání spambotů a self-botů</li>
                <li>Žádné úpravy Discord klienta</li>
                <li>Zákaz automatizovaných akcí</li>
              </ul>
            </div>
            <div className={styles.summaryCard}>
              <h3>Neautentické zapojení</h3>
              <ul>
                <li>Neprodávejte ani nenakupujte Discord účty</li>
                <li>Žádné umělé navyšování členství serveru</li>
                <li>Zákaz "join-for-join" schémat</li>
                <li>Neprodávejte vanity URL nebo oprávnění</li>
              </ul>
            </div>
            <div className={styles.summaryCard}>
              <h3>Jak se vyhnout porušení</h3>
              <ul>
                <li>Růst serveru organicky</li>
                <li>Neposílejte nevyžádané zprávy</li>
                <li>Neupravujte Discord klienta</li>
                <li>Neautomatizujte žádné akce</li>
              </ul>
            </div>
          </div>
          <p className={styles.summaryNote}>
            Toto je pouze stručné shrnutí. Pro úplné znění čtěte celý dokument níže.
          </p>
        </details>
      )}

      {/* Policy Body */}
      <div className={styles.policyBody}>
        {language === 'cs' ? (
          <>
            <section id="platform-abuse" className={styles.section}>
              <CopyLinkButton
                url={getSectionUrl('platform-abuse')}
                language={language}
                className={styles.sectionCopyLink}
              />
              <h2>
                <strong>Zneužívání platformy</strong>
              </h2>
              <p>
                Discord je místo, kde se přátelé scházejí, aby se bavili. V rámci našeho cíle
                poskytnout uživatelům dobrou zkušenost na platformě zakazujeme zneužívání platformy.
              </p>
              <p>
                Termín <strong>zneužívání platformy</strong> označuje činnosti, které narušují nebo
                mění zkušenost uživatelů Discordu. Odesílání nevyžádaných hromadných zpráv nebo
                interakcí (neboli <em>spam</em>) je jedním z nejčastějších způsobů narušení
                uživatelské zkušenosti. Spam může být odesílán automatizovanými účty navrženými pro
                tento účel (<em>spamboty</em>), běžnými uživatelskými účty, které ručně provádějí
                spamové akce, stejně jako uživatelskými účty upravenými k provádění automatizovaných
                akcí (<em>self-boty</em>). Provádění úprav Discord klienta za účelem spamu nebo z
                jakéhokoli jiného důvodu není podle této politiky povoleno.
              </p>
              <p>
                Podnikneme také kroky proti účtům registrovaným za účelem provádění zneužívání
                platformy. To zahrnuje automatizovanou registraci účtů jako skupiny pro spamování —
                bez ohledu na jejich rychlost odesílání zpráv. Odstraníme také servery usnadňující
                zneužívání platformy. To zahrnuje, ale není omezeno na prostory distribuující:
                spamboty, nástroje pro "raid" serverů, nástroje pro vytváření účtů, generátory
                tokenů, služby řešení CAPTCHA a další spamové nástroje.
              </p>
            </section>

            <section id="inauthentic-engagement" className={styles.section}>
              <CopyLinkButton
                url={getSectionUrl('inauthentic-engagement')}
                language={language}
                className={styles.sectionCopyLink}
              />
              <h2>
                <strong>Neautentické zapojení</strong>
              </h2>
              <p>
                Neautentické zapojení je úzce spojeno s problémem spamu, ale je od něj odlišné. Obě
                vedou k celkově nižší kvalitě zkušenosti pro uživatele.
              </p>
              <p>
                <strong>Zapojení</strong> v kontextu této politiky je definováno jako jakákoli
                interakce uživatele se službou nebo funkcemi produktu Discord. Zapojení budeme
                považovat za <strong>neautentické</strong>, pokud je zakoupeno, vyvoláno (například
                sliby finanční odměny) nebo podvodné (například zahrnuje falešné účty), nebo
                zfalšováno, například předstíráním splnění aktivity kvůli odměně.
              </p>
              <p>
                Jednotlivcům je zakázáno pokoušet se manipulovat s metrikami zapojení, uměle
                navyšovat členství serveru (například prostřednictvím "join-for-join", "invite
                rewards", nákupu falešných členů) a prodávat služby umělého zapojení pro online
                platformy. Jednotlivci také nesmí pokoušet se koupit nebo prodat aktiva Discord,
                jako jsou účty, uživatelská jména, servery, oprávnění serverů nebo vlastní odkazy na
                pozvánky na server (také nazývané "vanity URL").
              </p>
            </section>

            <section className={styles.examplesSection}>
              <button
                className={styles.dropdownToggle}
                onClick={() => setInauthenticExamplesOpen(!inauthenticExamplesOpen)}
                aria-expanded={inauthenticExamplesOpen}
                type="button"
              >
                <span>Například nesmíte zveřejňovat, sdílet nebo se zapojovat do:</span>
                <span className={styles.dropdownIcon}>{inauthenticExamplesOpen ? '−' : '+'}</span>
              </button>

              {inauthenticExamplesOpen && (
                <div className={styles.dropdownContent}>
                  <ul>
                    <li>
                      Nákup nebo vyvolávání zapojení; nebo používání falešných účtů k navýšení
                      těchto metrik
                    </li>
                    <li>
                      Nákup nebo vyvolávání připojení k serveru; nebo používání falešných účtů k
                      navýšení počtu členů
                    </li>
                    <li>
                      Pokus o prodej nebo nákup Discord účtu, uživatelského jména, serveru,
                      oprávnění serveru nebo vlastního odkazu na pozvánku na server (také nazývaného
                      "vanity URL")
                    </li>
                    <li>
                      Prodej nebo koordinace prodeje služeb umělého zapojení pro online platformy
                    </li>
                    <li>
                      Předstírání splnění Questů nebo jiných aktivit pomocí skriptů, falešných
                      aplikací či dalších nástrojů bez skutečného provedení požadované činnosti
                    </li>
                  </ul>
                </div>
              )}
            </section>

            <section id="how-to-avoid" className={styles.section}>
              <CopyLinkButton
                url={getSectionUrl('how-to-avoid')}
                language={language}
                className={styles.sectionCopyLink}
              />
              <h2>
                <strong>Jak se vyhnout porušení těchto politik</strong>
              </h2>
              <ul>
                <li>Neposílejte více nevyžádaných zpráv jiným uživatelům</li>
                <li>
                  Neupravujte Discord klienta z jakéhokoli důvodu — včetně automatizace akcí účtu
                  nebo změny vzhledu nebo rozložení Discordu
                </li>
                <li>Nechte svůj server růst organicky a uměle nenavyšujte počet jeho členů</li>
                <li>
                  Neprodávejte ani nenakupujte Discord účet, uživatelské jméno, server, oprávnění
                  serveru nebo vlastní odkaz na pozvánku na server (nebo "vanity URL")
                </li>
              </ul>
            </section>

            <section id="how-to-report" className={styles.section}>
              <CopyLinkButton
                url={getSectionUrl('how-to-report')}
                language={language}
                className={styles.sectionCopyLink}
              />
              <h2>
                <strong>Jak nahlásit porušení těchto politik</strong>
              </h2>
              <p>
                Nahlašování porušení bezpečnosti je kriticky důležité pro udržení bezpečnosti vás a
                širší komunity Discord. Všichni uživatelé Discordu mohou nahlásit porušení zásad v
                aplikaci podle pokynů{' '}
                <a
                  href="/discord/safety/reporting-abusive-behavior-to-discord"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  zde
                </a>
                . Pro více informací o těchto politikách se prosím podívejte na naše{' '}
                <a href="/discord/guidelines">Pokyny komunity</a>{' '}
                <GuidelineTooltip guidelineNumber="13" language={language}>
                  <a href="/discord/guidelines#guideline-13" className={styles.guidelineLink}>
                    #13
                  </a>
                </GuidelineTooltip>
                ,{' '}
                <GuidelineTooltip guidelineNumber="14" language={language}>
                  <a href="/discord/guidelines#guideline-14" className={styles.guidelineLink}>
                    #14
                  </a>
                </GuidelineTooltip>
                ,{' '}
                <GuidelineTooltip guidelineNumber="15" language={language}>
                  <a href="/discord/guidelines#guideline-15" className={styles.guidelineLink}>
                    #15
                  </a>
                </GuidelineTooltip>
                {' a '}
                <GuidelineTooltip guidelineNumber="16" language={language}>
                  <a href="/discord/guidelines#guideline-16" className={styles.guidelineLink}>
                    #16
                  </a>
                </GuidelineTooltip>
                .
              </p>
            </section>
          </>
        ) : (
          <>
            <section id="platform-abuse" className={styles.section}>
              <CopyLinkButton
                url={getSectionUrl('platform-abuse')}
                language={language}
                className={styles.sectionCopyLink}
              />
              <h2>
                <strong>Platform Abuse</strong>
              </h2>
              <p>
                Discord is a place where friends come to hang out. As part of our goal to provide
                users with a good on-platform experience, we prohibit platform abuse.
              </p>
              <p>
                The term <strong>platform abuse</strong> refers to activities that disrupt or alter
                the experience of Discord users. Sending unsolicited bulk messages or interactions
                (or <em>spam</em>) is one of the most common ways the user experience is disrupted.
                Spam can be sent by automated accounts designed for this purpose (<em>spambots</em>
                ), normal user accounts that manually execute spammy actions, as well as by user
                accounts modified to perform automated actions (<em>self-bots</em>). Making
                modifications to the Discord client for the purpose of spam or any other reason is
                not allowed under this policy.
              </p>
              <p>
                We will also take action on accounts registered for the purpose of carrying out
                platform abuse. This includes the automated registration of accounts as a group for
                spamming — regardless of their message-send rate. We will also remove servers
                facilitating platform abuse. This includes but is not limited to spaces
                distributing: spambots, server "raid" tools, account-creation tools, token
                generators, CAPTCHA-solving services, and other spam tools.
              </p>
            </section>

            <section id="inauthentic-engagement" className={styles.section}>
              <CopyLinkButton
                url={getSectionUrl('inauthentic-engagement')}
                language={language}
                className={styles.sectionCopyLink}
              />
              <h2>
                <strong>Inauthentic Engagement</strong>
              </h2>
              <p>
                Inauthentic engagement is closely tied to, but distinct from the issue of spam. Both
                result in an overall lower-quality experience for users.
              </p>
              <p>
                <strong>Engagement</strong> in the context of this policy is defined as any user
                interaction with Discord's service or product features. We will consider engagement
                to be <strong>inauthentic</strong>
                if it is purchased, induced (like promises of financial reward), or fraudulent (for
                example, it involves fake accounts), or fabricated by pretending to complete an
                activity to obtain rewards.
              </p>
              <p>
                Individuals are prohibited from attempting to manipulate engagement metrics,
                artificially inflating server membership (for example, via "join-for-join," "invite
                rewards," purchasing fake members), and selling artificial engagement services for
                online platforms. Individuals also may not attempt to buy or sell Discord assets,
                such as accounts, usernames, servers, server permissions, or custom server invite
                links (also called "vanity URLs").
              </p>
            </section>

            <section className={styles.examplesSection}>
              <button
                className={styles.dropdownToggle}
                onClick={() => setInauthenticExamplesOpen(!inauthenticExamplesOpen)}
                aria-expanded={inauthenticExamplesOpen}
                type="button"
              >
                <span>For example, you may not post, share, or engage in:</span>
                <span className={styles.dropdownIcon}>{inauthenticExamplesOpen ? '−' : '+'}</span>
              </button>

              {inauthenticExamplesOpen && (
                <div className={styles.dropdownContent}>
                  <ul>
                    <li>
                      Purchasing or inducing engagement; or, using fake accounts to boost such
                      metrics
                    </li>
                    <li>
                      Purchasing or inducing server joins; or, using fake accounts to boost member
                      count
                    </li>
                    <li>
                      Attempting to sell or purchase a Discord account, username, server, server
                      permissions, or a custom server invite link (also called a "vanity URL")
                    </li>
                    <li>
                      Selling or coordinating the sale of artificial engagement services for online
                      platforms
                    </li>
                    <li>
                      Faking Quest completion or other required activities through scripts, spoofed
                      apps or similar tools
                    </li>
                  </ul>
                </div>
              )}
            </section>

            <section id="how-to-avoid" className={styles.section}>
              <CopyLinkButton
                url={getSectionUrl('how-to-avoid')}
                language={language}
                className={styles.sectionCopyLink}
              />
              <h2>
                <strong>How to Avoid Violating These Policies</strong>
              </h2>
              <ul>
                <li>Don't send multiple unsolicited messages to other users</li>
                <li>
                  Don't modify the Discord client for any reason — including automating account
                  actions or altering the appearance or layout of Discord
                </li>
                <li>
                  Grow your server organically and don't artificially inflate its member count
                </li>
                <li>
                  Do not sell or purchase a Discord account, username, server, server permissions,
                  or a custom server invite link (or "vanity URL")
                </li>
              </ul>
            </section>

            <section id="how-to-report" className={styles.section}>
              <CopyLinkButton
                url={getSectionUrl('how-to-report')}
                language={language}
                className={styles.sectionCopyLink}
              />
              <h2>
                <strong>How to Report Violations of These Policies</strong>
              </h2>
              <p>
                Reporting safety violations is critically important to keeping you and the broader
                Discord community safe. All Discord users can report policy violations in the app by
                following the instructions{' '}
                <a
                  href="/discord/safety/reporting-abusive-behavior-to-discord"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                . For more information on these policies, please reference our{' '}
                <a href="/discord/guidelines">Community Guidelines</a>{' '}
                <GuidelineTooltip guidelineNumber="13" language={language}>
                  <a href="/discord/guidelines#guideline-13" className={styles.guidelineLink}>
                    #13
                  </a>
                </GuidelineTooltip>
                ,{' '}
                <GuidelineTooltip guidelineNumber="14" language={language}>
                  <a href="/discord/guidelines#guideline-14" className={styles.guidelineLink}>
                    #14
                  </a>
                </GuidelineTooltip>
                ,{' '}
                <GuidelineTooltip guidelineNumber="15" language={language}>
                  <a href="/discord/guidelines#guideline-15" className={styles.guidelineLink}>
                    #15
                  </a>
                </GuidelineTooltip>
                , and{' '}
                <GuidelineTooltip guidelineNumber="16" language={language}>
                  <a href="/discord/guidelines#guideline-16" className={styles.guidelineLink}>
                    #16
                  </a>
                </GuidelineTooltip>
                .
              </p>
            </section>
          </>
        )}
      </div>

      {/* Tags */}
      <div className={styles.tags}>
        <div className={styles.tagsLabel}>Tags</div>
        <div className={styles.tagsList}>
          <div className={styles.tag}>
            {language === 'cs' ? 'Integrita platformy' : 'Platform Integrity'}
          </div>
        </div>
      </div>
    </DiscordDocument>
  )
}

export default DiscordPlatformManipulationPolicyPage
